import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronLeft,
  faChevronRight,
} from "@nnr/fontawesome-sharp-light/icons/sharp/light";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";

const MAX_SCROLL_SPEED = 0.5;
const DECELERATION_RATE = 0.95;
const ACCELERATION_RATE = 0.05;
const CONTAINER_SCROLL_PADDING = 50;
const CONTAINER_SCROLL_DURATION = 300;

export type InfiniteScrollCarouselHandle = {
  animateToPosition: (target: number) => void;
  getScrollContainer: () => HTMLDivElement | null;
  getTranslateX: () => number;
  pause: () => void;
  setKeepPaused: (value: boolean) => void;
};

type Props = {
  children: React.ReactNode;
  resetKey?: unknown;
  className?: string;
};

export const InfiniteScrollCarousel = forwardRef<
  InfiniteScrollCarouselHandle,
  Props
>(({ children, resetKey, className }, ref) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [paused, setPaused] = useState(false);
  const [keepPaused, setKeepPausedState] = useState(false);
  const scrollAnimationRef = useRef<number | null>(null);
  const translateXRef = useRef<number>(0);
  const currentSpeedRef = useRef<number>(MAX_SCROLL_SPEED);
  const manualScrollAnimationRef = useRef<number | null>(null);
  const isManualScrollingRef = useRef<boolean>(false);

  // Reset position when items change
  useEffect(() => {
    translateXRef.current = 0;
    if (contentRef.current) {
      contentRef.current.style.transform = `translateX(0px)`;
    }
  }, [resetKey]);

  // Check if content overflows container
  useEffect(() => {
    const checkOverflow = () => {
      if (scrollContainerRef.current && contentRef.current) {
        const containerWidth = scrollContainerRef.current.offsetWidth;
        const contentWidth = contentRef.current.scrollWidth;
        setIsOverflowing(contentWidth > containerWidth);
      }
    };

    checkOverflow();
    window.addEventListener("resize", checkOverflow);
    return () => window.removeEventListener("resize", checkOverflow);
  }, [resetKey]);

  const animateToPosition = (targetPosition: number) => {
    if (!isOverflowing) return;
    if (isManualScrollingRef.current) return;
    if (!scrollContainerRef.current || !contentRef.current) return;

    const content = contentRef.current;
    const singleSetWidth = content.scrollWidth / 3;

    let startPosition = translateXRef.current;
    const minPosition = -singleSetWidth * 2;
    const maxPosition = 0;
    const middleSetStart = -singleSetWidth;

    if (startPosition <= minPosition) {
      startPosition = middleSetStart;
      translateXRef.current = startPosition;
      content.style.transform = `translateX(${startPosition}px)`;
    } else if (startPosition >= maxPosition) {
      startPosition = middleSetStart;
      translateXRef.current = startPosition;
      content.style.transform = `translateX(${startPosition}px)`;
    }

    const startTime = performance.now();
    isManualScrollingRef.current = true;

    const animateScroll = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / CONTAINER_SCROLL_DURATION, 1);
      const easeProgress = 1 - Math.pow(1 - progress, 3);

      translateXRef.current =
        startPosition + (targetPosition - startPosition) * easeProgress;

      if (translateXRef.current <= minPosition) {
        const offset = translateXRef.current - minPosition;
        translateXRef.current = middleSetStart + offset;
      } else if (translateXRef.current >= maxPosition) {
        const offset = translateXRef.current - maxPosition;
        translateXRef.current = middleSetStart + offset;
      }

      content.style.transform = `translateX(${translateXRef.current}px)`;

      if (progress < 1) {
        manualScrollAnimationRef.current = requestAnimationFrame(animateScroll);
      } else {
        isManualScrollingRef.current = false;
        manualScrollAnimationRef.current = null;
      }
    };

    manualScrollAnimationRef.current = requestAnimationFrame(animateScroll);
  };

  const scrollByContainer = (direction: "left" | "right") => {
    if (!scrollContainerRef.current) return;
    const containerWidth = scrollContainerRef.current.offsetWidth;
    const startPosition = translateXRef.current;
    const targetPosition =
      direction === "right"
        ? startPosition - containerWidth + CONTAINER_SCROLL_PADDING
        : startPosition + containerWidth - CONTAINER_SCROLL_PADDING;
    animateToPosition(targetPosition);
    setKeepPausedState(false);
  };

  // Auto-scroll animation
  useEffect(() => {
    if (!isOverflowing || !contentRef.current) return;

    const content = contentRef.current;
    const singleSetWidth = content.scrollWidth / 3;

    if (translateXRef.current === 0) {
      translateXRef.current = -singleSetWidth;
      content.style.transform = `translateX(${translateXRef.current}px)`;
    }

    const animate = () => {
      if (!isManualScrollingRef.current) {
        if (paused) {
          currentSpeedRef.current *= DECELERATION_RATE;
          if (currentSpeedRef.current < 0.01) currentSpeedRef.current = 0;
        } else {
          currentSpeedRef.current +=
            (MAX_SCROLL_SPEED - currentSpeedRef.current) * ACCELERATION_RATE;
          if (Math.abs(currentSpeedRef.current - MAX_SCROLL_SPEED) < 0.01) {
            currentSpeedRef.current = MAX_SCROLL_SPEED;
          }
        }

        translateXRef.current -= currentSpeedRef.current;

        if (translateXRef.current <= -singleSetWidth * 2) {
          translateXRef.current = -singleSetWidth;
        } else if (translateXRef.current >= 0) {
          translateXRef.current = -singleSetWidth;
        }

        content.style.transform = `translateX(${translateXRef.current}px)`;
      }

      scrollAnimationRef.current = requestAnimationFrame(animate);
    };

    scrollAnimationRef.current = requestAnimationFrame(animate);

    return () => {
      if (scrollAnimationRef.current) {
        cancelAnimationFrame(scrollAnimationRef.current);
      }
    };
  }, [isOverflowing, paused]);

  const handleUnpause = () => {
    if (keepPaused) return;
    setPaused(false);
  };

  useImperativeHandle(ref, () => ({
    animateToPosition,
    getScrollContainer: () => scrollContainerRef.current,
    getTranslateX: () => translateXRef.current,
    pause: () => setPaused(true),
    setKeepPaused: (value: boolean) => setKeepPausedState(value),
  }));

  return (
    <div className={cn("relative w-full", className)}>
      <div
        className="flex flex-1 flex-row flex-nowrap gap-2 overflow-hidden"
        ref={scrollContainerRef}
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={handleUnpause}
      >
        <div
          className="flex flex-row flex-nowrap gap-2"
          ref={contentRef}
          style={{ willChange: "transform" }}
        >
          {isOverflowing && children}
          {children}
          {isOverflowing && children}
        </div>
      </div>
      {isOverflowing && (
        <>
          <div className="from-background-1 pointer-events-none absolute top-0 left-0 h-full w-10 bg-gradient-to-r to-transparent" />
          <div className="from-background-1 pointer-events-none absolute top-0 right-0 h-full w-10 bg-gradient-to-l to-transparent" />
          <div className="absolute top-1/2 -left-10 -translate-y-1/2 ease-in-out">
            <Button
              size="icon"
              variant="ghost"
              aria-label="Scroll left"
              title="Scroll left"
              onClick={() => scrollByContainer("left")}
              onMouseEnter={() => setPaused(true)}
              onMouseLeave={handleUnpause}
            >
              <FontAwesomeIcon icon={faChevronLeft} />
            </Button>
          </div>
          <div className="absolute top-1/2 -right-10 -translate-y-1/2 ease-in-out">
            <Button
              size="icon"
              variant="ghost"
              aria-label="Scroll right"
              title="Scroll right"
              onClick={() => scrollByContainer("right")}
              onMouseEnter={() => setPaused(true)}
              onMouseLeave={handleUnpause}
            >
              <FontAwesomeIcon icon={faChevronRight} />
            </Button>
          </div>
        </>
      )}
    </div>
  );
});

InfiniteScrollCarousel.displayName = "InfiniteScrollCarousel";
